// EmailCenter.jsx
import { useState } from 'react'
import {
    Send, Mail, Inbox, CheckCircle2, Clock, Plus, Search, Star, Trash2,
    Archive, Users, MousePointerClick, MoreHorizontal, Reply, Forward,
    FileText, ChevronLeft, LayoutList, Tag, StarIcon
} from 'lucide-react'
import './EmailCenter.css'

const INITIAL_EMAILS = [
    { id: 'EML-001', subject: 'Welcome to Netra Technosys!', type: 'Transactional', from: 'system@netratechnosys.com', recipients: 1, sent: 1, opened: 1, clicked: 0, status: 'sent', date: 'May 5', preview: 'Welcome aboard! Your account has been successfully created.', body: 'Welcome aboard!\n\nYour account has been successfully created. Get started by exploring the dashboard and setting up your profile.\n\nIf you need any help, our support team is available 24/7.\n\nBest regards,\nNetra Technosys Team', starred: false },
    { id: 'EML-002', subject: 'Your May Invoice is Ready', type: 'Billing', from: 'billing@netratechnosys.com', recipients: 580, sent: 580, opened: 423, clicked: 380, status: 'sent', date: 'May 4', preview: 'Your invoice for May 2025 is now available. Total amount due: $79.00.', body: 'Dear Customer,\n\nYour invoice for May 2025 is now available.\n\nInvoice Details:\n- Plan: Pro\n- Amount: $79.00\n- Due Date: May 15, 2025\n\nPlease review and process the payment at your earliest convenience.\n\nThank you for your business!', starred: true },
    { id: 'EML-003', subject: 'New Feature: Advanced Export', type: 'Marketing', from: 'news@netratechnosys.com', recipients: 1250, sent: 1250, opened: 870, clicked: 520, status: 'sent', date: 'May 3', preview: "We're excited to announce our new Advanced Export feature!", body: "We're excited to announce our new Advanced Export feature!\n\nWhat's new:\n✓ Export to CSV, PDF, Excel\n✓ Custom date ranges\n✓ Scheduled exports\n✓ Email delivery\n\nLog in to try it out today!", starred: false },
    { id: 'EML-004', subject: 'Subscription Renewal Reminder', type: 'Billing', from: 'billing@netratechnosys.com', recipients: 210, sent: 210, opened: 198, clicked: 160, status: 'sent', date: 'May 2', preview: 'Your subscription will renew in 7 days.', body: 'Dear Customer,\n\nYour subscription will renew in 7 days.\n\nRenewal Details:\n- Plan: Enterprise\n- Amount: $299.00\n- Renewal Date: May 12, 2025\n\nPlease ensure your payment method is up to date to avoid any service interruption.', starred: false },
    { id: 'EML-005', subject: 'June Newsletter Draft', type: 'Marketing', from: 'me', recipients: 1500, sent: 0, opened: 0, clicked: 0, status: 'draft', date: 'May 6', preview: 'June highlights: New dashboard redesign, performance improvements…', body: 'June Newsletter Draft\n\nHighlights this month:\n- New dashboard redesign (coming soon)\n- 40% performance improvement\n- Upcoming webinar: Analytics Best Practices\n- Customer spotlight: TechFlow Inc.\n\n[DRAFT — not sent yet]', starred: false },
    { id: 'EML-006', subject: 'System Maintenance Alert', type: 'Transactional', from: 'system@netratechnosys.com', recipients: 1250, sent: 0, opened: 0, clicked: 0, status: 'scheduled', date: 'May 8', preview: 'Scheduled maintenance on May 10, 2025 from 2:00 AM to 4:00 AM UTC.', body: 'Dear Users,\n\nWe will be performing scheduled maintenance on:\n\nDate: May 10, 2025\nTime: 2:00 AM — 4:00 AM UTC\n\nDuring this window, services may be temporarily unavailable.\n\nWe apologize for any inconvenience.\n\nNetra Technosys Team', starred: true },
]

const TYPE_META = {
    Transactional: { color: '#06B6D4', bg: 'rgba(6,182,212,0.12)' },
    Billing: { color: '#4F46E5', bg: 'rgba(79,70,229,0.12)' },
    Marketing: { color: '#A855F7', bg: 'rgba(168,85,247,0.12)' },
}

const STATUS_META = {
    sent: { color: '#10B981', bg: 'rgba(16,185,129,0.12)', label: 'Sent', Icon: CheckCircle2 },
    draft: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', label: 'Draft', Icon: FileText },
    scheduled: { color: '#06B6D4', bg: 'rgba(6,182,212,0.12)', label: 'Scheduled', Icon: Clock },
}

const RECIP_MAP = { 'All Users': 1250, 'Pro Users': 580, 'Enterprise Users': 210, 'Trial Users': 120 }

// Mobile panels: 'folders' | 'list' | 'detail'
export default function EmailCenter() {
    const [emails, setEmails] = useState(INITIAL_EMAILS)
    const [folder, setFolder] = useState('all')
    const [search, setSearch] = useState('')
    const [selectedId, setSelectedId] = useState('EML-001')
    const [composeOpen, setComposeOpen] = useState(false)
    const [mobilePanel, setMobilePanel] = useState('list') // 'list' | 'detail'
    const [form, setForm] = useState({ subject: '', type: 'Marketing', recipients: 'All Users', body: '' })

    const filtered = emails.filter(e => {
        if (folder === 'starred') return e.starred
        if (['sent', 'draft', 'scheduled'].includes(folder)) return e.status === folder
        if (['Transactional', 'Billing', 'Marketing'].includes(folder)) return e.type === folder
        return true
    }).filter(e => !search ||
        e.subject.toLowerCase().includes(search.toLowerCase()) ||
        e.preview.toLowerCase().includes(search.toLowerCase())
    )

    const selected = emails.find(e => e.id === selectedId) || null
    const openRate = e => (e.sent ? Math.round((e.opened / e.sent) * 100) : 0)
    const sentEmails = emails.filter(e => e.status === 'sent')
    const totalSent = sentEmails.reduce((s, e) => s + e.sent, 0)
    const totalOpened = sentEmails.reduce((s, e) => s + e.opened, 0)

    const toggleStar = (id, ev) => {
        ev?.stopPropagation()
        setEmails(prev => prev.map(e => e.id === id ? { ...e, starred: !e.starred } : e))
    }

    const deleteEmail = id => {
        const next = filtered.find(e => e.id !== id)
        setEmails(prev => prev.filter(e => e.id !== id))
        if (selectedId === id) { setSelectedId(next?.id || null); setMobilePanel('list') }
    }

    const sendDraft = id => setEmails(prev => prev.map(e =>
        e.id === id ? { ...e, status: 'sent', sent: e.recipients, opened: 0, clicked: 0 } : e
    ))

    const handleSend = () => {
        if (!form.subject.trim()) return
        const cnt = RECIP_MAP[form.recipients] || 100
        const newEmail = { id: 'EML-' + Date.now(), subject: form.subject, type: form.type, from: 'me', recipients: cnt, sent: cnt, opened: 0, clicked: 0, status: 'sent', date: 'Now', preview: form.body.slice(0, 70) + '…', body: form.body, starred: false }
        setEmails(prev => [newEmail, ...prev])
        setSelectedId(newEmail.id); setFolder('all'); setMobilePanel('list')
        setForm({ subject: '', type: 'Marketing', recipients: 'All Users', body: '' })
        setComposeOpen(false)
    }

    const handleSaveDraft = () => {
        const newEmail = { id: 'EML-D' + Date.now(), subject: form.subject.trim() || '(No subject)', type: form.type, from: 'me', recipients: 0, sent: 0, opened: 0, clicked: 0, status: 'draft', date: 'Now', preview: form.body.slice(0, 70) || '(Empty draft)', body: form.body || '(Empty draft)', starred: false }
        setEmails(prev => [newEmail, ...prev])
        setSelectedId(newEmail.id)
        setForm({ subject: '', type: 'Marketing', recipients: 'All Users', body: '' })
        setComposeOpen(false)
    }

    const handleReply = () => { setForm({ subject: 'Re: ' + selected?.subject, type: 'Transactional', recipients: 'All Users', body: '\n\n--- Original ---\n' + (selected?.body || '') }); setComposeOpen(true) }
    const handleForward = () => { setForm({ subject: 'Fwd: ' + selected?.subject, type: 'Transactional', recipients: 'All Users', body: '\n\n--- Forwarded ---\n' + (selected?.body || '') }); setComposeOpen(true) }

    const selectEmail = (id) => {
        setSelectedId(id)
        setMobilePanel('detail')
    }

    const changeFolder = (key) => {
        setFolder(key)
        setMobilePanel('list') // always go to list on mobile when folder changes
    }

    const SIDEBAR = [
        { key: 'all', label: 'All Mail', Icon: Mail, count: emails.length },
        { key: 'sent', label: 'Sent', Icon: Send, count: emails.filter(e => e.status === 'sent').length },
        { key: 'draft', label: 'Drafts', Icon: FileText, count: emails.filter(e => e.status === 'draft').length },
        { key: 'scheduled', label: 'Scheduled', Icon: Clock, count: emails.filter(e => e.status === 'scheduled').length },
        { key: 'starred', label: 'Starred', Icon: Star, count: emails.filter(e => e.starred).length },
    ]

    // Mobile bottom nav tabs
    const MOB_NAV = [
        { key: 'list', label: 'Inbox', Icon: LayoutList },
        { key: 'sent', label: 'Sent', Icon: Send },
        { key: 'starred', label: 'Starred', Icon: StarIcon },
        { key: 'draft', label: 'Drafts', Icon: FileText },
    ]

    return (
        <div className="email-page">

            {/* Top bar — hidden on mobile (bottom nav replaces it) */}
            <div className="email-topbar">
                <h1 className="page-title">Email Center</h1>
            </div>

            {/* Shell — detail-open class drives mobile CSS panel switching */}
            <div className={`email-shell${mobilePanel === 'detail' ? ' detail-open' : ''}`}>

                {/* ── Sidebar ── */}
                <div className="email-sidebar">
                    <button className="btn btn-primary" style={{ marginBottom: 8 }} onClick={() => setComposeOpen(true)}>
                        <Plus size={14} /> Compose
                    </button>
                    <div className="email-section-label">Mailbox</div>
                    {SIDEBAR.map(item => (
                        <button key={item.key} className={`email-sidebar-button${folder === item.key ? ' active' : ''}`} onClick={() => changeFolder(item.key)}>
                            <item.Icon size={14} strokeWidth={1.8} />
                            <span style={{ flex: 1 }}>{item.label}</span>
                            {item.count > 0 && (
                                <span style={{ fontSize: 10, fontWeight: 700, background: folder === item.key ? 'var(--accent-hover)' : 'rgba(255,255,255,0.05)', color: folder === item.key ? '#fff' : 'var(--text3)', padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>
                                    {item.count}
                                </span>
                            )}
                        </button>
                    ))}
                    <div className="email-separator" />
                    <div className="email-section-label">Type</div>
                    {Object.entries(TYPE_META).map(([type, meta]) => (
                        <button key={type} className={`email-sidebar-button${folder === type ? ' active' : ''}`} onClick={() => changeFolder(type)}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                            <span style={{ flex: 1 }}>{type}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.05)', color: 'var(--text3)', padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>
                                {emails.filter(e => e.type === type).length}
                            </span>
                        </button>
                    ))}
                </div>

                {/* ── Email list ── */}
                <section className="email-list">
                    <div className="email-list-search">
                        <div className="email-search-wrapper">
                            <Search size={13} />
                            <input className="email-search-field" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                    </div>
                    <div className="email-list-body">
                        {filtered.length === 0 && <div className="email-empty-state">No emails found</div>}
                        {filtered.map(e => {
                            const tc = TYPE_META[e.type]
                            const sc = STATUS_META[e.status]
                            return (
                                <div key={e.id} className={`email-list-item${selectedId === e.id ? ' active' : ''}`} onClick={() => selectEmail(e.id)}>
                                    <button className="email-action-btn" style={{ padding: 2, width: 26, height: 26, border: 'none', flexShrink: 0 }} onClick={ev => toggleStar(e.id, ev)}>
                                        <Star size={13} fill={e.starred ? '#F59E0B' : 'none'} color={e.starred ? '#F59E0B' : 'var(--text3)'} />
                                    </button>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                            <span className="subject">{e.subject}</span>
                                            <span className="meta">{e.date}</span>
                                        </div>
                                        <div className="preview">{e.preview}</div>
                                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                            <span className="badge" style={{ background: tc.bg, color: tc.color }}>{e.type}</span>
                                            <span className="badge" style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </section>

                {/* ── Email detail ── */}
                <section className="email-detail">
                    {/* Mobile back button */}
                    <div className="email-mobile-header">
                        <button className="emh-back" onClick={() => setMobilePanel('list')}>
                            <ChevronLeft size={16} /> Back
                        </button>
                        {selected && (
                            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {selected.subject}
                            </span>
                        )}
                    </div>

                    {selected ? (
                        <>
                            {/* Header */}
                            <div className="email-detail-header">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                                    <h2>{selected.subject}</h2>
                                    <div className="email-detail-actions">
                                        {[
                                            { icon: <Star size={14} fill={selected.starred ? '#F59E0B' : 'none'} color={selected.starred ? '#F59E0B' : 'var(--text3)'} />, action: ev => toggleStar(selected.id, ev), label: 'Star' },
                                            { icon: <Archive size={14} />, action: () => deleteEmail(selected.id), label: 'Archive' },
                                            { icon: <Trash2 size={14} />, action: () => deleteEmail(selected.id), label: 'Delete' },
                                            { icon: <MoreHorizontal size={14} />, action: () => { }, label: 'More' },
                                        ].map((btn, i) => (
                                            <button key={i} className="email-action-btn" onClick={btn.action} aria-label={btn.label}>{btn.icon}</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="email-detail-meta">
                                    <div className="email-detail-avatar">NT</div>
                                    <div style={{ flex: 1 }}>
                                        <div className="sender-name">{selected.from}</div>
                                        <div className="sender-meta">to {selected.recipients > 1 ? `${selected.recipients.toLocaleString()} recipients` : 'you'}</div>
                                    </div>
                                    <span className="sender-meta">{selected.date}, 2025</span>
                                </div>
                            </div>



                            {/* Body */}
                            <div className="email-body">
                                <pre>{selected.body}</pre>
                            </div>

                            {/* Action row */}
                            <div className="email-action-row">
                                <button className="btn btn-outline" onClick={handleReply}><Reply size={13} /> Reply</button>
                                <button className="btn btn-outline" onClick={handleForward}><Forward size={13} /> Forward</button>
                                {selected.status === 'draft' && (
                                    <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={() => sendDraft(selected.id)}>
                                        <Send size={13} /> Send Now
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="email-empty-panel">
                            <Mail size={40} strokeWidth={1} />
                            <span style={{ fontSize: '0.8125rem' }}>Select an email to read</span>
                        </div>
                    )}
                </section>

            </div>

            {/* ── Mobile bottom nav ── */}
            {mobilePanel !== 'detail' && (
                <nav className="email-mobile-nav">
                    <div className="email-mobile-nav-inner">
                        {MOB_NAV.slice(0, 2).map(item => (
                            <button key={item.key} className={`emn-btn${(folder === item.key || item.key === mobilePanel) ? '' : ''} ${mobilePanel === 'list' && folder === item.key ? 'active' : mobilePanel === 'list' && item.key === 'list' && !['sent', 'draft', 'starred'].includes(folder) ? 'active' : ''}`}
                                onClick={() => { changeFolder(item.key === 'list' ? 'all' : item.key) }}>
                                <item.Icon size={20} strokeWidth={1.8} />
                                {item.label}
                            </button>
                        ))}
                        <button className="emn-compose" onClick={() => setComposeOpen(true)} aria-label="Compose">
                            <Plus size={22} color="#fff" />
                        </button>
                        {MOB_NAV.slice(2).map(item => (
                            <button key={item.key} className={`emn-btn${folder === item.key && mobilePanel === 'list' ? ' active' : ''}`}
                                onClick={() => changeFolder(item.key)}>
                                <item.Icon size={20} strokeWidth={1.8} />
                                {item.label}
                            </button>
                        ))}
                    </div>
                </nav>
            )}
            {/* ── Compose ── */}
            {composeOpen && (
                <div className="compose-modal">
                    <div className="compose-header">
                        <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>New Email</span>
                        <button style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', fontSize: 22, lineHeight: 1 }} onClick={() => setComposeOpen(false)}>×</button>
                    </div>
                    <div className="compose-body">
                        {[
                            { id: 'recipients', opts: ['All Users', 'Pro Users', 'Enterprise Users', 'Trial Users'] },
                            { id: 'type', opts: ['Marketing', 'Billing', 'Transactional'] },
                        ].map(f => (
                            <select key={f.id} value={form[f.id]} onChange={e => setForm(p => ({ ...p, [f.id]: e.target.value }))}>
                                {f.opts.map(o => <option key={o}>{o}</option>)}
                            </select>
                        ))}
                        <input placeholder="Subject" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} />
                    </div>
                    <textarea placeholder="Write your message…" value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} style={{ flex: 1, minHeight: 160, background: 'var(--bg-surface)', border: 'none', resize: 'none', outline: 'none', color: 'var(--text1)', fontFamily: 'inherit', fontSize: '0.8125rem', lineHeight: 1.7, padding: '12px 14px' }} />
                    <div className="compose-footer">
                        <button className="btn btn-primary" onClick={handleSend}><Send size={13} /> Send</button>
                        <button className="btn btn-outline" onClick={handleSaveDraft}><FileText size={13} /> Save Draft</button>
                        <button onClick={() => setComposeOpen(false)} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text3)', padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 'auto', width: 30, height: 30 }}>
                            <Trash2 size={13} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}